import React from "react";
import { useHistory } from "react-router-dom";
import SideBarItem from "./SideBarItem";
import { useGlobalContext } from "../../UserContext/UserContext";
import { clearAuthStorage } from "../../services/sessionService";
import { AppButton, AppButtonLink } from "../AppButton/AppButton";

function CollapsibleGroup({ title, isOpen, onToggle, items }) {
  function handleToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  }

  return (
    <li className="list-none">
      <button
        type="button"
        onClick={handleToggle}
        className={`sidebar-group-toggle w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 ${
          isOpen ? 'sidebar-group-toggle-open' : 'sidebar-group-toggle-collapsed'
        }`}
      >
        <span>{title}</span>
        <svg
          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <ul className="mt-1.5 space-y-1 ml-2 pl-3 border-l-2 border-blue-200">
          {items.map((item) => (
            <SideBarItem
              key={item.to}
              to={item.to}
              text={item.text}
              variant="sub"
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function SideBar() {
  const {
    setUserToken,
    setShowLogin,
    showMyBotSetup,
    setShowMyBotSetup,
    showCurrentlyRunning,
    setShowCurrentlyRunning,
  } = useGlobalContext();
  const history = useHistory();

  const handleLogout = () => {
    clearAuthStorage();
    setUserToken(null);
    setShowLogin(true);
    history.push("/");
  };

  const botSetupItems = [
    { to: "/unified-setup", text: "Algo Bot" },
    { to: "/grid", text: "Grid Bot" },
    { to: "/scheduled", text: "Scheduled Bot" },
  ];

  const currentlyRunningItems = [
    { to: "/bots", text: "Bots" },
    { to: "/orderTemplates", text: "Order Templates" },
    { to: "/withdrawTemplates", text: "Withdraw Templates" },
    { to: "/monitors", text: "Monitors" },
    { to: "/webhooks", text: "WebHook" },
    { to: "/symbols", text: "Symbols" },
  ];

  const ordersLabel = localStorage.getItem("hasFutures") === "true"
    ? "Active Orders and Positions"
    : "Active Orders";

  return (
    <nav className="sidebar-shell flex-1 overflow-y-scroll main_bg w-full">
      <ul className="flex flex-col gap-0.5 p-[1vw]">
        <SideBarItem to="/dashboard" text="Dashboard" />

        <CollapsibleGroup
          title="My Bot Setup"
          isOpen={showMyBotSetup}
          onToggle={() => setShowMyBotSetup(!showMyBotSetup)}
          items={botSetupItems}
        />

        <CollapsibleGroup
          title="Currently Running"
          isOpen={showCurrentlyRunning}
          onToggle={() => setShowCurrentlyRunning(!showCurrentlyRunning)}
          items={currentlyRunningItems}
        />

        <SideBarItem to="/orders" text={ordersLabel} />
        <SideBarItem to="/wallet" text="Wallet" />
        <SideBarItem to="/reports" text="Reports" />
        <SideBarItem to="/backtest" text="Backtest" />
        <SideBarItem to="/settings" text="Settings" />

        <li className="list-none mt-4 pt-3 border-t border-blue-200">
          <div className="sidebar-auth-actions px-1">
            <AppButton variant="logout" size="sm" fullWidth onClick={handleLogout}>
              Logout
            </AppButton>
            <AppButtonLink to="/" variant="secondary" size="sm" fullWidth>
              Go back to homepage
            </AppButtonLink>
          </div>
        </li>
      </ul>

      <style>{`
        .sidebar-shell {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #ffffff;
          border-right: 1px solid #bfdbfe;
          box-shadow: 2px 0 12px rgba(37, 99, 235, 0.06);
        }

        .sidebar-group-toggle {
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: #1e3a8a;
          background: #eff6ff;
          border: 1px solid #dbeafe;
        }

        .sidebar-group-toggle:hover {
          background: #dbeafe;
          color: #1e40af;
        }

        .sidebar-group-toggle-open {
          background: #dbeafe;
          border-color: #93c5fd;
          color: #1e40af;
        }

        .sidebar-group-toggle-collapsed {
          background: #f8fafc;
          border-color: #e2e8f0;
          color: #334155;
        }

        .sidebar-link {
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.01em;
          line-height: 1.4;
        }

        .sidebar-link-sub {
          font-size: 0.8125rem;
        }

        .sidebar-link-active-main {
          background: #dbeafe;
          color: #1e3a8a;
          font-weight: 600;
          box-shadow: inset 3px 0 0 #2563eb;
        }

        .sidebar-link-active-sub {
          background: #dbeafe;
          color: #1e3a8a;
          font-weight: 600;
          box-shadow: inset 3px 0 0 #2563eb;
        }

        .sidebar-shell::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-shell::-webkit-scrollbar-thumb {
          background-color: #93c5fd;
          border-radius: 3px;
        }

        .sidebar-shell::-webkit-scrollbar-thumb:hover {
          background-color: #60a5fa;
        }

        .sidebar-shell {
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #f8fafc;
        }
      `}</style>
    </nav>
  );
}

export default SideBar;